import { Track } from "../shared/music";
import { mediaSrc } from "./bridge";
import { flashToast } from "./dom";
import { t } from "./i18n";

// the one player for the whole app: a single audio element that keeps playing while people move about, a queue,
// and the media session, which is how the lock screen, bluetooth headsets and car stereos (including carplay's now
// playing screen) show the song and send play, pause, next, previous and seek back

export type RepeatMode = "off" | "all" | "one";

export interface PlayerState {
  track: Track | null;
  playing: boolean;
  time: number;
  duration: number;
  queue: Track[];
  index: number;
  shuffle: boolean;
  repeat: RepeatMode;
  volume: number;
}

const PREFS_KEY = "chix-music-prefs";
const SKIP_SECONDS = 10;
type Listener = (state: PlayerState, what: "track" | "play" | "time" | "queue" | "prefs") => void;

const listeners: Listener[] = [];
let audio: HTMLAudioElement | null = null;
let queue: Track[] = [];
let order: number[] = [];
let at = 0;
let shuffle = false;
let repeat: RepeatMode = "off";
let volume = 0.8;
let failures = 0;
let lastPosition = 0;
let onDuration: (id: string, seconds: number) => void = () => {};

function readPrefs(): void {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") as { volume?: number; shuffle?: boolean; repeat?: string };
    if (Number.isFinite(saved.volume)) volume = Math.min(1, Math.max(0, Number(saved.volume)));
    shuffle = saved.shuffle === true;
    repeat = saved.repeat === "all" || saved.repeat === "one" ? saved.repeat : "off";
  } catch {
    // defaults
  }
}

function savePrefs(): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ volume, shuffle, repeat }));
  } catch {
    // private windows can refuse storage
  }
}

readPrefs();

function element(): HTMLAudioElement {
  if (audio) return audio;
  audio = document.createElement("audio");
  audio.preload = "metadata";
  audio.setAttribute("playsinline", "");
  audio.volume = volume;
  audio.hidden = true;
  document.body.append(audio);
  audio.addEventListener("timeupdate", () => {
    emit("time");
    positionState();
  });
  audio.addEventListener("loadedmetadata", () => {
    const track = current();
    if (track && Number.isFinite(audio!.duration) && audio!.duration > 0 && Math.abs(audio!.duration - track.duration) > 1) {
      track.duration = Math.round(audio!.duration);
      onDuration(track.id, track.duration);
    }
    emit("time");
    positionState();
  });
  audio.addEventListener("play", () => {
    failures = 0;
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
    emit("play");
  });
  audio.addEventListener("pause", () => {
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
    emit("play");
  });
  audio.addEventListener("ended", () => advance(true));
  audio.addEventListener("error", () => {
    if (!current()) return;
    flashToast(t("could not play {name}", { name: current()!.title }));
    // a few bad files in a row stop the queue instead of skipping through all of it
    if (++failures < 3) advance(true);
  });
  return audio;
}

function current(): Track | null {
  return queue[order[at]] || null;
}

export function playerState(): PlayerState {
  const player = audio;
  return {
    track: current(),
    playing: Boolean(player && !player.paused && !player.ended && current()),
    time: player?.currentTime || 0,
    duration: Number.isFinite(player?.duration) && player!.duration > 0 ? player!.duration : current()?.duration || 0,
    queue,
    index: order[at] ?? 0,
    shuffle,
    repeat,
    volume
  };
}

function emit(what: Parameters<Listener>[1]): void {
  const state = playerState();
  listeners.forEach((listener) => listener(state, what));
}

export function onPlayer(listener: Listener): void {
  listeners.push(listener);
}

// the view learns a song's real length and saves it, so lists can show it
export function onTrackLength(handler: (id: string, seconds: number) => void): void {
  onDuration = handler;
}

function arrange(first: number): void {
  order = queue.map((_, index) => index);
  if (shuffle) {
    for (let index = order.length - 1; index > 0; index--) {
      const pick = Math.floor(Math.random() * (index + 1));
      [order[index], order[pick]] = [order[pick], order[index]];
    }
    // the song being played leads, the rest follow in a new order
    order.splice(order.indexOf(first), 1);
    order.unshift(first);
  }
  at = order.indexOf(first);
}

function load(autoplay: boolean): void {
  const track = current();
  const player = element();
  if (!track) return;
  player.src = mediaSrc(track.audioUrl || "");
  player.load();
  setSession(track);
  emit("track");
  if (autoplay) void player.play().catch(() => emit("play"));
}

export function playQueue(tracks: Track[], first: number): void {
  if (!tracks.length || !tracks[first]) return;
  queue = tracks.slice();
  arrange(first);
  load(true);
  emit("queue");
}

// the library changed under the queue: keep playing the same song, refresh the rest
export function refreshQueue(tracks: Track[]): void {
  const now = current();
  if (!now) return;
  const byId = new Map(tracks.map((track) => [track.id, track]));
  const still = queue.filter((track) => byId.has(track.id)).map((track) => byId.get(track.id)!);
  const here = still.findIndex((track) => track.id === now.id);
  if (here < 0) return;
  queue = still;
  arrange(here);
  emit("queue");
}

export function toggle(): void {
  const player = element();
  if (!current()) return;
  if (player.paused) void player.play().catch(() => emit("play"));
  else player.pause();
}

export function pause(): void {
  audio?.pause();
}

function advance(fromEnd: boolean): void {
  if (!queue.length) return;
  if (fromEnd && repeat === "one") {
    element().currentTime = 0;
    void element().play().catch(() => emit("play"));
    return;
  }
  if (at + 1 >= order.length) {
    if (repeat === "all" || !fromEnd) {
      if (shuffle) arrange(order[order.length - 1]);
      at = 0;
      load(true);
      return;
    }
    // the end of the queue: stop on the first song, ready to start again
    at = 0;
    load(false);
    return;
  }
  at += 1;
  load(true);
}

export function next(): void {
  advance(false);
}

// back to the start of a song, and only past the first few seconds back a song
export function previous(): void {
  const player = element();
  if (player.currentTime > 3 || at === 0) {
    player.currentTime = 0;
    return;
  }
  at -= 1;
  load(true);
}

export function seek(seconds: number): void {
  const player = element();
  if (!current() || !Number.isFinite(seconds)) return;
  player.currentTime = Math.max(0, Math.min(seconds, Number.isFinite(player.duration) ? player.duration : seconds));
  emit("time");
}

export function setVolume(value: number): void {
  volume = Math.min(1, Math.max(0, value));
  element().volume = volume;
  savePrefs();
  emit("prefs");
}

export function toggleShuffle(): void {
  shuffle = !shuffle;
  const now = order[at];
  if (queue.length) arrange(now);
  savePrefs();
  emit("prefs");
}

export function cycleRepeat(): void {
  repeat = repeat === "off" ? "all" : repeat === "all" ? "one" : "off";
  savePrefs();
  emit("prefs");
}

// --- the media session: what a car stereo or lock screen shows and sends

// a label made from the title for songs with no cover, so a lock screen is never left with a blank square
export function labelHue(text: string): number {
  let hash = 0;
  for (const letter of text) hash = (hash * 31 + letter.charCodeAt(0)) >>> 0;
  return hash % 360;
}

export function labelGradient(text: string): string {
  const hue = labelHue(text);
  return `linear-gradient(135deg, hsl(${hue} 62% 34%), hsl(${(hue + 55) % 360} 72% 58%))`;
}

export function initials(text: string): string {
  return text.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase() || "♪";
}

function labelPicture(track: Track): string {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  const hue = labelHue(track.title);
  const gradient = ctx.createLinearGradient(0, 0, 512, 512);
  gradient.addColorStop(0, `hsl(${hue} 62% 34%)`);
  gradient.addColorStop(1, `hsl(${(hue + 55) % 360} 72% 58%)`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 512, 512);
  ctx.fillStyle = "rgba(255,255,255,0.88)";
  ctx.font = "700 200px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(initials(track.title), 256, 268);
  return canvas.toDataURL("image/png");
}

function artworkFor(track: Track): MediaImage[] {
  if (track.coverUrl) {
    const src = new URL(mediaSrc(track.coverUrl), location.href).href;
    const type = /\.png$/i.test(track.cover) ? "image/png" : /\.webp$/i.test(track.cover) ? "image/webp" : "image/jpeg";
    return [{ src, sizes: "512x512", type }];
  }
  const label = labelPicture(track);
  return label ? [{ src: label, sizes: "512x512", type: "image/png" }] : [];
}

function setSession(track: Track): void {
  if (!("mediaSession" in navigator)) return;
  navigator.mediaSession.metadata = new MediaMetadata({ title: track.title, artist: track.artist, album: track.album, artwork: artworkFor(track) });
}

function positionState(): void {
  if (!("mediaSession" in navigator) || !audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
  // once a second is plenty for a car's progress bar
  if (Math.abs(audio.currentTime - lastPosition) < 1 && !audio.paused) return;
  lastPosition = audio.currentTime;
  try {
    navigator.mediaSession.setPositionState({ duration: audio.duration, playbackRate: audio.playbackRate || 1, position: Math.min(audio.currentTime, audio.duration) });
  } catch {
    // some browsers refuse odd values mid-load
  }
}

export function setupMediaSession(): void {
  if (!("mediaSession" in navigator)) return;
  const handlers: Array<[MediaSessionAction, MediaSessionActionHandler]> = [
    ["play", () => toggleTo(true)],
    ["pause", () => toggleTo(false)],
    ["stop", () => toggleTo(false)],
    ["previoustrack", () => previous()],
    ["nexttrack", () => next()],
    ["seekbackward", (details) => seek((audio?.currentTime || 0) - (details.seekOffset || SKIP_SECONDS))],
    ["seekforward", (details) => seek((audio?.currentTime || 0) + (details.seekOffset || SKIP_SECONDS))],
    ["seekto", (details) => seek(details.seekTime || 0)]
  ];
  for (const [action, handler] of handlers) {
    try {
      navigator.mediaSession.setActionHandler(action, handler);
    } catch {
      // an action this browser does not know
    }
  }
}

function toggleTo(playing: boolean): void {
  const player = element();
  if (!current()) return;
  if (playing && player.paused) void player.play().catch(() => emit("play"));
  if (!playing && !player.paused) player.pause();
}
